import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Newspaper, Calendar } from 'lucide-react';
import { SEO } from '../components/SEO';
import { imgUrl } from '../utils/imgUrl';
import { PR_POSTS } from '../data/prPosts';
import { ShareButtons } from '../components/ShareButtons';
import { ShopTheLook } from '../components/ShopTheLook';

const SITE = 'https://rezvanifurniturestudio.com';

const fmtDate = (d) =>
  new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

const PostBody = ({ body }) => (
  <div>
    {body.split(/\n\s*\n/).map((block, i) => {
      const text = block.trim();
      if (!text) return null;
      if (text.startsWith('## ')) {
        return (
          <h2 key={i} className="text-lg md:text-lg font-bold text-gray-900 mt-8 mb-3">
            {text.slice(3)}
          </h2>
        );
      }
      return (
        <p key={i} className="text-base text-gray-700 leading-relaxed mb-4">
          {text}
        </p>
      );
    })}
  </div>
);

const PostDetail = ({ post }) => (
  <article className="max-w-3xl mx-auto" data-testid={`pr-post-${post.slug}`}>
    <SEO
      title={`${post.title} | Rezvani Furniture Studio`}
      description={post.excerpt}
      canonical={`https://rezvanifurniturestudio.com/pr/${post.slug}`}
    />
    <Link to="/pr" data-testid="pr-back-link" className="inline-flex items-center gap-1.5 text-sm font-medium text-cyan-600 hover:text-cyan-700 mb-6">
      <ArrowLeft size={16} /> All PR &amp; News
    </Link>
    {post.image && (
      <div className="rounded-3xl overflow-hidden shadow-xl mb-8" data-testid="pr-post-hero-image">
        <img src={imgUrl(post.image, 1600)} alt={post.imageAlt || post.title} className="w-full aspect-[21/9] object-cover" />
      </div>
    )}
    <p className="flex items-center gap-1.5 text-xs text-gray-500 mb-3">
      <Calendar size={13} /> {fmtDate(post.date)}
    </p>
    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">{post.title}</h1>
    <div className="mb-8 pb-6 border-b border-gray-200">
      <ShareButtons url={`${SITE}/pr/${post.slug}`} title={post.title} image={post.image} />
    </div>
    <PostBody body={post.body} />
    <div className="mt-8 pt-6 border-t border-gray-200">
      <ShareButtons url={`${SITE}/pr/${post.slug}`} title={post.title} image={post.image} testId="pr-share-buttons-bottom" />
    </div>
    <ShopTheLook slugs={post.products || []} />
    <div className="mt-10 p-6 bg-cyan-50 border border-cyan-100 rounded-2xl">
      <p className="text-sm text-gray-700 mb-3 font-medium">Ready to make your pool the best spot in the backyard?</p>
      <Link
        to="/products"
        data-testid="pr-shop-cta"
        className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl text-sm font-semibold hover:shadow-xl transition-all duration-300"
      >
        Shop In-Pool Furniture <ArrowRight size={16} />
      </Link>
    </div>
  </article>
);

const PostList = () => (
  <div className="max-w-4xl mx-auto">
    <SEO
      title="PR & News | Rezvani Furniture Studio"
      description="Press, news and guides from Rezvani Furniture Studio — in-pool furniture tips, tanning ledge ideas and luxury backyard inspiration."
      canonical="https://rezvanifurniturestudio.com/pr"
    />
    <div className="mb-10">
      <span className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold text-cyan-700 bg-cyan-50 px-4 py-2 rounded-full uppercase tracking-wider">
        <Newspaper size={14} /> PR &amp; News
      </span>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mt-4 mb-3">Public Relations</h1>
      <p className="text-base text-gray-600 max-w-2xl">
        News, guides and ideas from Rezvani Furniture Studio — everything from tanning ledge tips to building your own backyard resort.
      </p>
    </div>
    <div className="space-y-6" data-testid="pr-post-list">
      {PR_POSTS.map((post) => (
        <Link
          key={post.slug}
          to={`/pr/${post.slug}`}
          data-testid={`pr-card-${post.slug}`}
          className="group block bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-xl hover:border-cyan-200 transition-all duration-300"
        >
          {post.image && (
            <div className="overflow-hidden">
              <img
                src={imgUrl(post.image, 1200)}
                alt={post.imageAlt || post.title}
                loading="lazy"
                className="w-full aspect-[21/9] object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </div>
          )}
          <div className="p-6 sm:p-8">
            <p className="flex items-center gap-1.5 text-xs text-gray-500 mb-2">
              <Calendar size={13} /> {fmtDate(post.date)}
            </p>
            <h2 className="text-lg md:text-lg font-bold text-gray-900 group-hover:text-cyan-700 transition-colors duration-300 mb-2">
              {post.title}
            </h2>
            <p className="text-sm text-gray-600 leading-relaxed mb-4">{post.excerpt}</p>
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-cyan-600">
              Read post <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </span>
          </div>
        </Link>
      ))}
    </div>
  </div>
);

export default function PR() {
  const { slug } = useParams();
  const post = slug ? PR_POSTS.find((p) => p.slug === slug) : null;
  if (slug && !post) return <Navigate to="/pr" replace />;

  return (
    <div className="min-h-screen bg-gradient-to-b from-cyan-50/40 to-white pt-28 sm:pt-32 pb-16 sm:pb-24">
      <div className="px-4 sm:px-6 lg:px-8">{post ? <PostDetail post={post} /> : <PostList />}</div>
    </div>
  );
}
